<!-- eslint-disable max-len -->
<template>
  <div class="view-contents">
    <b-alert
      :show="dismissCountDown"
      dismissible
      variant="success"
      @dismissed="dismissCountDown=0"
      @dismiss-count-down="countDownChanged"
    >
      Site has been downloaded
    </b-alert>
    <b-alert
      :show="successDismissCountDown"
      dismissible
      variant="success"
      @dismissed="successDismissCountDown=0"
      @dismiss-count-down="successCountDownChanged"
    >
      {{ successMessage }}
    </b-alert>
    <div v-if="currentSite">
      <div class="row title-row">
        <div class="col page-title">
          Site: <a target="insightly"
                   :href="`${config.insightly.siteUrl}${currentSite.Id}`"
                   v-if="config">
                  {{ currentSite.RecordName }}
                </a>
        </div>
      </div>
      <div class="row section-header">
        <div class="col right-col">
        <ul class="nav nav-pills justify-content-end">
          <li class="nav-item">
            <a class="nav-link  rounded-pill active" href="#">
              <span>Site Overview</span>
            </a>
          </li>
          <li class="nav-item">
            <router-link class="nav-link rounded-pill" :to="{ name: 'ManagementPlans', params: { id: currentSite.Id } }">
              <span>Management Plans</span>
            </router-link>
          </li>
          <li class="nav-item">
            <b-link class="nav-link rounded-pill" v-if="!isCurrentSiteDownloaded" @click="addSiteToDb">
              <span>
                Download Site<b-icon icon='download' class='download-site-icon'/>
              </span>
            </b-link>
          </li>
        </ul>
      </div>
      </div>
      <div class="row site-section-wrapper">
        <div class="col-4">
          <Details/>
        </div>
        <div class="col-8 right-col">
          <Map/>
        </div>
      </div>
      <div class="row site-action-row">
        <div class="col action-cell text-center">
          <div class="site-action-title">
            First Site Visit
          </div>
          <b-alert
            :show="warningDismissCountDown"
            dismissible
            variant="warning"
            @dismissed="warningDismissCountDown=0"
            @dismiss-count-down="warningCountDownChanged"
          >
            You are offline, First Site Visit is only saved on your machine.
            Please submit again when you are back online.
          </b-alert>
          <div class="site-action-status">
            <span class="font-weight-bold">Status: </span>{{ firstSiteVisitStatus() }}
          </div>
          <div>
            <b-button pill
                      size="sm"
                      variant="primary"
                      class="site-action-button"
                      :class="{ 'edit-button': siteVisit }"
                      :to="{ name: 'SiteVisit', id: currentSite.Id }">
              {{ firstSiteVisitButtonText() }}
            </b-button>
            <b-button pill
                      v-if="siteVisit && siteVisit.finished && siteVisit.needsOnlineSubmit"
                      size="sm"
                      variant="primary"
                      class="site-action-button"
                      @click="submitSiteVisit">
              Submit
            </b-button>
          </div>
        </div>
        <div class="col action-cell text-center">
          <div class="site-action-title">
            Site Assessment
          </div>
          <div class="site-action-status">
            <template v-if="!siteVisit || !siteVisit.finished">
              First Site Visit must be finished before Site Assessment can be started
            </template>
            <template v-else>
              <span class="font-weight-bold">Status: </span>{{ siteAssessmentStatus() }}
            </template>
          </div>
          <div>
            <b-button pill
                      :disabled="!siteVisit || !siteVisit.finished"
                      size="sm"
                      variant="primary"
                      class="site-action-button"
                      :class="{ 'edit-button': siteAssessment && siteAssessment.createdAt }"
                      :to="{ name: 'SiteAssessment', id: currentSite.Id }">
              {{ siteAssessmentButtonText() }}
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable max-len, padded-blocks */
import { mapState, mapGetters } from 'vuex';
import { Store, set } from 'idb-keyval';
import AlertManager from '@/mixins/AlertManager';
import Details from '@/components/sites/Details.vue';
import Map from '@/components/sites/Map.vue';

export default {
  name: 'Site',
  components: {
    Details,
    Map,
  },
  mixins: [AlertManager],
  computed: {
    ...mapState(['currentSite', 'config']),
    ...mapState('SiteVisit', {
      siteVisit: (state) => state.siteVisit,
      siteVisitSubmitSuccess: (state) => state.submitSuccess,
    }),
    ...mapState('SiteAssessment', {
      siteAssessment: (state) => state.siteAssessment,
      siteAssessmentSubmitSuccess: (state) => state.submitSuccess,
    }),
    ...mapGetters(['isCurrentSiteDownloaded']),
  },
  data() {
    return {
      dismissCountDown: 0,
      siteStore: null,
      successMessage: '',
    };
  },
  mounted() {
    this.$store.dispatch('fetchConfiguration').then(() => {
      this.$store.dispatch('fetchSiteAsync', parseInt(this.$route.params.id, 10)).then(async () => {
        this.$store.dispatch('SiteVisit/fetchSiteVisit',
          {
            currentSite: this.currentSite,
            currentUser: this.$store.getters['User/getCurrentUser'].name,
            initializeVisit: false,
          });
      });
    });
    this.$store.dispatch('SiteAssessment/fetchExistingSiteAssessment', parseInt(this.$route.params.id, 10));

    if (this.siteVisitSubmitSuccess) {
      this.successMessage = 'First Site Visit has been submitted';
      this.showSuccessAlert();
      this.$store.dispatch('SiteVisit/setSubmitSuccess', false);
    }

    if (this.siteAssessmentSubmitSuccess) {
      this.successMessage = 'Site Assessment has been submitted';
      this.showSuccessAlert();
      this.$store.dispatch('SiteAssessment/setSubmitSuccess', false);
    }
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  methods: {
    addSiteToDb() {
      const customStore = new Store('AcornDB', 'sites');
      set(this.currentSite.Id, this.currentSite, customStore);
      this.$store.dispatch('SiteVisit/persistCurrentSiteVisit');
      this.showAlert();
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    showAlert() {
      this.dismissCountDown = 5;
    },
    firstSiteVisitStatus() {
      if (this.siteVisit) {
        const withDate = (status, date) => (
          date ? `${status} ${date.toLocaleDateString()}` : status
        );

        if (!this.siteVisit.finished) {
          return withDate('Started', this.siteVisit.localCreatedAt);
        }
        if (this.siteVisit.needsOnlineSubmit) {
          return withDate('Completed (not submitted)', this.siteVisit.localCompletedAt);
        }
        return withDate('Completed', this.siteVisit.createdAt && new Date(this.siteVisit.createdAt));
      }
      return 'Not Started';
    },
    firstSiteVisitButtonText() {
      if (this.siteVisit) {
        return 'Edit';
      }
      return 'Start';
    },
    siteAssessmentStatus() {
      if (
        this.siteAssessment
        && this.siteAssessment.status
        && this.siteAssessment.submittedAt
        && this.siteAssessment.submittedAt != null
        && this.siteAssessment.submittedAt !== '0001-01-01T00:00:00'
        && this.siteAssessment.submittedAt !== '0001-01-01T00:00:00Z'
      ) {
        return `${this.siteAssessment.status} ${new Date(this.siteAssessment.submittedAt).toLocaleDateString()}`;
      }
      if (this.siteAssessment && this.siteAssessment.createdAt) {
        return `Started ${new Date(this.siteAssessment.createdAt).toLocaleDateString()}`;
      }
      return 'Not Started';
    },
    siteAssessmentButtonText() {
      if (this.siteAssessment && this.siteAssessment.createdAt) {
        return 'Edit';
      }
      return 'Start';
    },
    async submitSiteVisit() {
      if (navigator.onLine) {
        this.isSubmittingSiteVisit = true;
        await this.$store.dispatch('SiteVisit/postSiteVisit', this.siteVisit);
        this.isSubmittingSiteVisit = false;
        this.showSuccessAlert();
      } else {
        this.siteVisit.needsOnlineSubmit = true;
        this.siteVisit.localCompletedAt = new Date();
        this.$store.dispatch('SiteVisit/setSiteVisit', this.siteVisit);
        this.showWarningAlert();
      }
    },
  },
};
</script>
